import { Dispatch, SetStateAction } from 'react';
import { useGroupMergeAction } from '@/components/action-buttons/use-group-merge-action';
import { useMarketplaceApi } from '@/context/marketplace.context';
import type { GroupMergePayload } from '@/components/action-buttons/use-group-merge-modal-orchestration';

type UseGroupBrandsMergeActionArgs = {
    accessToken: string;
    setPickedBrandGroups?: (val: string[]) => void;
    refetchReviewsAndProducts: () => Promise<void>;
    setIsGroupsMergeLoading: Dispatch<SetStateAction<boolean>>;
};

type UseGroupProductsMergeActionArgs = {
    accessToken: string;
    selectionSessionId: string;
    setPickedProducts?: (val: string[]) => void;
    refetchReviewsAndProducts: () => Promise<void>;
    setIsGroupsMergeLoading: Dispatch<SetStateAction<boolean>>;
};

type UseGroupBrandsMergeActionReturn = {
    groupPickedBrands: (payload: GroupMergePayload) => Promise<boolean>;
};

type UseGroupProductsMergeActionReturn = {
    groupPickedProducts: (payload: GroupMergePayload) => Promise<boolean>;
};

export const useGroupBrandsMergeAction = ({
    accessToken,
    setPickedBrandGroups,
    refetchReviewsAndProducts,
    setIsGroupsMergeLoading,
}: UseGroupBrandsMergeActionArgs): UseGroupBrandsMergeActionReturn => {
    const { brandsApi } = useMarketplaceApi();
    const { groupPicked: groupPickedBrands } = useGroupMergeAction({
        setPickedGroupIds: setPickedBrandGroups,
        refetchReviewsAndProducts,
        setIsGroupsMergeLoading,
        mergeGroups: (payload) => brandsApi.mergeBrandGroups(payload, { authToken: accessToken }),
    });

    return {
        groupPickedBrands,
    };
};

export const useGroupProductsMergeAction = ({
    accessToken,
    selectionSessionId,
    setPickedProducts,
    refetchReviewsAndProducts,
    setIsGroupsMergeLoading,
}: UseGroupProductsMergeActionArgs): UseGroupProductsMergeActionReturn => {
    const { productsApi } = useMarketplaceApi();
    const { groupPicked: groupPickedProducts } = useGroupMergeAction({
        setPickedGroupIds: setPickedProducts,
        refetchReviewsAndProducts,
        setIsGroupsMergeLoading,
        mergeGroups: (payload) => productsApi.mergeProductGroups(payload, { authToken: accessToken }),
        syncSelectionAfterMerge: selectionSessionId
            ? async () => {
                  await productsApi.setProductSelectionSession(selectionSessionId, [], {
                      authToken: accessToken,
                  });
              }
            : undefined,
    });

    return {
        groupPickedProducts,
    };
};
